"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _quill = _interopRequireDefault(require("quill"));
var _config = require("./config");
var _styles = require("./styles");
var _Text = _interopRequireDefault(require("../Text"));
var _IconErrorOutline = _interopRequireDefault(require("../../icons/General/IconErrorOutline"));
var _react2 = require("@emotion/react");
var _core = require("quill/core");
var _LinkSanitizer = require("./classes/LinkSanitizer");
var _dompurify = _interopRequireDefault(require("dompurify"));
require("quill/dist/quill.snow.css");
var _embed = _interopRequireDefault(require("quill/blots/embed"));
var _jsxRuntime = require("@emotion/react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; } /** @jsxImportSource @emotion/react */
class SmartBreakBlot extends _embed.default {
  static blotName = 'smartbreak';
  static tagName = 'br';
}
// Colors
_quill.default.register(_quill.default.import('attributors/style/color'), true);
// Background colors
_quill.default.register(_quill.default.import('attributors/style/background'), true);
//Text direction
_quill.default.register(_quill.default.import('attributors/style/direction'), true);
//Alignment
_quill.default.register(_quill.default.import('attributors/style/align'), true);
// Image
_quill.default.register('attributors/style/image', true);
// Video
_quill.default.register('attributors/style/video', true);
// Strike
_quill.default.register('attributors/style/strike', true);
// Table
_quill.default.register('attributors/style/table', true);
// Underline
_quill.default.register('attributors/style/underline', true);
// Bold
_quill.default.register('attributors/style/bold', true);
// Smart break
_quill.default.register(SmartBreakBlot, true);
// Text sizes
const Size = _quill.default.import('attributors/style/size');
Size.whitelist = ['0.75em', '1em', '1.5em', '2.5em'];
_quill.default.register(Size, true);

// Font
const Font = _quill.default.import('formats/font');
Font.whitelist = ['Source Sans Pro', 'sans-serif'];
_quill.default.register(Font, true);
_quill.default.register(_LinkSanitizer.CustomLinkSanitizer, true);
function brMatcher(node) {
  if (!(node instanceof HTMLBRElement)) {
    return new _core.Delta();
  }
  return new _core.Delta().insert({
    smartbreak: true
  });
}
var _ref = process.env.NODE_ENV === "production" ? {
  name: "108zowc",
  styles: "display:flex;gap:4px;align-items:baseline"
} : {
  name: "1jvq0lt-WordCount",
  styles: "display:flex;gap:4px;align-items:baseline;label:WordCount;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var _ref2 = process.env.NODE_ENV === "production" ? {
  name: "1soh0gv",
  styles: "margin-top:4px"
} : {
  name: "14rbig3-QuillEditor",
  styles: "margin-top:4px;label:QuillEditor;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
const QuillEditor = ({
  height,
  width,
  maxHeight,
  minHeight = '150px',
  maxWidth,
  minWidth,
  placeholder,
  initialValue,
  overrideValue,
  label,
  readOnly = false,
  maxlenght = 0,
  wordCount = true,
  type = 'basic',
  customModules = {
    toolbar: _config.basicToolbar
  },
  withSoftBreakStyle = true,
  formats = ['header', 'size', 'bold', 'italic', 'underline', 'list', 'indent', 'link', 'image', 'align'],
  bounds,
  onChange,
  onFocus,
  onBlur,
  invalidMessage,
  className,
  disabled,
  pasteAsText,
  resizable
}) => {
  const [quill, setQuill] = (0, _react.useState)(null);
  const isMounted = (0, _react.useRef)(false);
  const quillRef = (0, _react.useRef)(null);
  const defaultValueRef = (0, _react.useRef)(initialValue);
  const editorId = (0, _react.useRef)(new Date().getTime() + (100 * Math.random()).toFixed(0));
  const [currentLength, setCurrentLength] = (0, _react.useState)(initialValue?.length || 0);
  const isWordCount = wordCount && maxlenght > 0;
  const {
    alert
  } = (0, _react2.useTheme)();
  const toolbars = {
    basic: _config.basicToolbar,
    rich: _config.richToolbar,
    custom: customModules.toolbar
  };
  const handleBackspaceWithIndentetList = (0, _react.useCallback)((range, _context, quill) => {
    if (!quill) return true;
    const [line, offset] = quill.getLine(range.index);
    const formats = line.formats();
    if (formats.list && formats.indent) {
      // Check if we're at the beginning of the line
      if (range.index === range.index - offset) {
        // Remove indent
        const indent = parseInt(formats.indent, 10);
        quill.format('indent', indent ? indent - 1 : false);
        quill.format('list', formats.list);
        return false;
      } else if (range.length === 0 && offset > 0) {
        // If there's content and the cursor is not at the beginning,
        // let Quill handle it normally
        return true;
      } else {
        // Remove indent
        quill.format('indent', false);
        quill.format('list', formats.list);
        return false;
      }
    }
    return true;
  }, [quill]);
  const modules = {
    toolbar: {
      container: toolbars[type || 'basic'],
      handlers: {
        align: function (value) {
          const quill = this.quill;
          const range = quill.getSelection();
          if (!range) return;
          const [line] = quill.getLine(range.index);
          if (!line) return;
          const formats = line.formats();

          // If we're in a list, don't allow alignment changes
          if (formats.list) {
            // Adds disabled styles to alignment buttons
            return;
          }

          // Otherwise, proceed with normal alignment
          quill.format('align', value);
        }
      }
    },
    clipboard: {
      matchVisual: false,
      onPaste: e => {
        if (pasteAsText && quill) {
          e.preventDefault();
          const text = e.clipboardData?.getData('text/plain') || '';
          const range = quill.getSelection();
          if (range) {
            quill.insertText(range.index, text);
          }
        }
      }
    },
    keyboard: {
      scrollingContainer: null,
      bindings: {
        custom: {
          key: 'Enter',
          shiftKey: false,
          handler: (range, context, editor) => {
            const quill = editor;
            if (!quill) return false;
            quill.insertText(range.index, '\n', _quill.default.sources.USER);
            quill.setSelection(range.index + 1, _quill.default.sources.SILENT);
            Object.keys(context.format).forEach(name => {
              if (name === 'code' || name === 'link') return;
              if (Array.isArray(context.format[name])) return;
              quill.format(name, context.format[name], _quill.default.sources.USER);
            });
            quill.focus();
            return false;
          }
        },
        linebreak: {
          key: 'Enter',
          shiftKey: true,
          handler: (range, context, editor) => {
            const quill = editor;
            if (!quill) return false;
            const currentLeaf = quill.getLeaf(range.index)[0] || null;
            const nextLeaf = quill.getLeaf(range.index + 1)[0] || null;
            quill.insertEmbed(range.index, 'smartbreak', true, _quill.default.sources.USER);
            if (nextLeaf === null || currentLeaf && nextLeaf && currentLeaf.parent !== nextLeaf.parent) {
              quill.insertEmbed(range.index, 'smartbreak', true, _quill.default.sources.USER);
            }
            quill.setSelection(range.index + 1, _quill.default.sources.SILENT);
            return false;
          }
        }
      }
    }
  };
  const formatOutputHtml = (0, _react.useCallback)(html => {
    let formattedHtml = html.replace(/<p[^>]*?><\/p>/g, '<br />');
    formattedHtml = formattedHtml.replace(/&nbsp;/g, ' ');
    return formattedHtml;
  }, []);
  const handleChange = (0, _react.useCallback)(quill => {
    if (!quill) return;
    const html = _dompurify.default.sanitize(quill.getSemanticHTML(), {
      USE_PROFILES: {
        html: true
      }
    });
    const formattedHtml = formatOutputHtml(html);
    const length = quill.getLength() - 1;
    if (length === 0) {
      setCurrentLength(0);
    } else {
      setCurrentLength(formattedHtml.length);
    }
    onChange?.(formattedHtml, length);
  }, [onChange, quill]);
  const WordCount = () => {
    return (0, _jsxRuntime.jsxs)("div", {
      css: _ref,
      children: [(0, _jsxRuntime.jsxs)(_Text.default, {
        children: [currentLength, " / ", maxlenght]
      }), (0, _jsxRuntime.jsxs)(_Text.default, {
        small: true,
        color: "textSecondary",
        children: ["(NB: Formatering er inkludert i maksgrensen)", ' ']
      })]
    });
  };
  (0, _react.useEffect)(() => {
    if (isMounted.current) return;
    quillRef.current = new _quill.default(`#editor${editorId.current}`, {
      debug: false,
      theme: 'snow',
      bounds: bounds || `#editor${editorId.current}`,
      modules: {
        ...modules,
        keyboard: {
          bindings: {
            shiftenter: {
              key: 'Enter',
              shiftKey: true,
              handler: () => {
                if (!quillRef.current) return true;
                const range = quillRef.current.getSelection();
                if (!range) return true;
                quillRef.current.insertEmbed(range.index, 'smartbreak', true);
                quillRef.current.setSelection(range.index + 1, 0);
                return false;
              }
            }
          }
        }
      },
      formats,
      placeholder,
      readOnly
    });
    quillRef.current.keyboard.addBinding({
      key: 'Backspace'
    }, {
      format: {
        list: true,
        indent: true
      }
    }, (range, context) => {
      return handleBackspaceWithIndentetList(range, context, quillRef.current);
    });

    // Add Enter binding
    quillRef.current.keyboard.addBinding({
      key: 'Enter',
      shiftKey: false
    }, (range, context) => {
      const quill = quillRef.current;
      if (!quill) return false;
      quill.insertText(range.index, '\n', _quill.default.sources.USER);
      quill.setSelection(range.index + 1, _quill.default.sources.SILENT);
      Object.keys(context.format).forEach(name => {
        if (name === 'code' || name === 'link') return;
        if (Array.isArray(context.format[name])) return;
        quill.format(name, context.format[name], _quill.default.sources.USER);
      });
      quill.focus();
      return false;
    });

    // Add Shift+Enter binding
    quillRef.current.keyboard.addBinding({
      key: 'Enter',
      shiftKey: true
    }, range => {
      const quill = quillRef.current;
      if (!quill) return false;
      const currentLeaf = quill.getLeaf(range.index)[0] || null;
      const nextLeaf = quill.getLeaf(range.index + 1)[0] || null;
      quill.insertEmbed(range.index, 'smartbreak', true, _quill.default.sources.USER);
      if (nextLeaf === null || currentLeaf && nextLeaf && currentLeaf.parent !== nextLeaf.parent) {
        quill.insertEmbed(range.index, 'smartbreak', true, _quill.default.sources.USER);
      }
      quill.setSelection(range.index + 1, _quill.default.sources.SILENT);
      return false;
    });
    quillRef.current.root.style.touchAction = 'pan-y';
    quillRef.current.root.style.overflowY = 'auto';
    quillRef.current.disable();
    quillRef.current.enable(true);
    // Move the last binding in the list, to the top. Ensure that the custom binding is called first.
    quillRef.current.keyboard.bindings['Backspace'].unshift(quillRef.current.keyboard.bindings['Backspace'].pop());
    quillRef.current.keyboard.bindings['Tab'].unshift(quillRef.current.keyboard.bindings['Tab'].pop());
    quillRef.current.keyboard.addBinding({
      key: 'Escape'
    }, () => {
      // Remove focus from editor
      quillRef.current.blur();
    });
    if (defaultValueRef.current) {
      const delta = quillRef.current.clipboard.convert({
        html: defaultValueRef.current
      });
      const formattedDelta = delta.ops.map(op => {
        if (op.attributes && op.attributes.indent) {
          op.attributes.indent = parseInt(op.attributes.indent, 10);
        }
        return op;
      });
      quillRef.current.setContents(formattedDelta);
    }
    if (onFocus || onBlur) {
      quillRef.current.on(_quill.default.events.SELECTION_CHANGE, (range, oldRange, source) => {
        const hasFocus = range && !oldRange;
        const hasBlur = !range && oldRange;
        if (onFocus && hasFocus) onFocus(quillRef.current.root.innerHTML);
        if (onBlur && hasBlur) onBlur(quillRef.current.root.innerHTML);
      });
    }
    quillRef.current.scroll.register(SmartBreakBlot);
    quillRef.current.clipboard.addMatcher('BR', brMatcher);
    isMounted.current = true;
    setQuill(quillRef.current);
    return () => {
      quillRef.current = null;
    };
  }, [quillRef]);
  (0, _react.useEffect)(() => {
    if (!quill) return;
    quill.on(_quill.default.events.TEXT_CHANGE, () => handleChange(quill));
  }, [quill, handleChange]);
  (0, _react.useEffect)(() => {
    if (!quill) return;
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (!pasteAsText) return delta;
      let ops = [];
      delta.ops.forEach(op => {
        if (op.insert && typeof op.insert === 'string') {
          ops.push({
            insert: op.insert
          });
        }
      });
      delta.ops = ops;
      return delta;
    });
  }, [quill, pasteAsText]);
  (0, _react.useEffect)(() => {
    if (quill && overrideValue !== undefined) {
      const currentContent = quill.root.innerHTML;
      if (currentContent !== overrideValue) {
        const delta = quill.clipboard.convert({
          html: overrideValue
        });
        quill.setContents(delta);
      }
    }
  }, [quill, overrideValue]);
  (0, _react.useEffect)(() => {
    if (quill && (disabled || readOnly)) {
      quill.enable(false);
    } else if (quill) {
      quill.enable(true);
    }
  }, [readOnly, quill]);
  return (0, _jsxRuntime.jsxs)("div", {
    className: className,
    css: theme => [_styles.styles.default(theme, maxHeight, minHeight, width, minWidth, maxWidth, height, resizable, withSoftBreakStyle)],
    children: [label && (0, _jsxRuntime.jsx)(_styles.Label, {
      children: label
    }), (0, _jsxRuntime.jsx)("div", {
      id: `editor${editorId.current}`
    }), isWordCount && (0, _jsxRuntime.jsx)("div", {
      css: _ref2,
      children: (0, _jsxRuntime.jsx)(WordCount, {})
    }), invalidMessage && (0, _jsxRuntime.jsx)(_styles.ErrorWrapper, {
      children: (0, _jsxRuntime.jsxs)(_Text.default, {
        color: alert,
        children: [(0, _jsxRuntime.jsx)(_IconErrorOutline.default, {
          size: "16px",
          color: alert
        }), " ", invalidMessage]
      })
    })]
  });
};
var _default = exports.default = QuillEditor;